/*********************************************************************************
 * Copyright (C) 2025 VECROS Technologies Pvt. Ltd. <legal@vecros.com>
 * Proprietary and confidential.
 **********************************************************************************/

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  InputBase,
  useTheme,
  ClickAwayListener,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import DashboardIcon from "@mui/icons-material/Dashboard";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import DevicesOutlinedIcon from "@mui/icons-material/DevicesOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import HttpOutlinedIcon from "@mui/icons-material/HttpOutlined";
import SpeedOutlinedIcon from "@mui/icons-material/SpeedOutlined";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import CalculateOutlinedIcon from "@mui/icons-material/CalculateOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import MemoryOutlinedIcon from "@mui/icons-material/MemoryOutlined";
import { useAssistant } from "./assistant";
import { useAssistantRecords } from "../hooks/useAssistantRecords";
import { ROUTES } from "../routes";
import { CONTENT_SEARCH_INDEX } from "../generated/searchContentIndex";

const ACCENT = "#006BCE";
const RECENTS_KEY = "vcs_recent_searches_developer";

/** Static index of pages / sections — the fast, always-available "objects". */
const INDEX = [
  { title: "Dashboard", subtitle: "Home & overview", path: ROUTES.home, Icon: DashboardIcon, kw: "home dashboard overview" },
  { title: "Documentation", subtitle: "Developer docs", path: ROUTES.docs, Icon: MenuBookOutlinedIcon, kw: "docs guide reference api" },
  { title: "Downloads", subtitle: "SDKs & downloads", path: ROUTES.downloads, Icon: DownloadOutlinedIcon, kw: "download sdk installer firmware" },
  { title: "Devices", subtitle: "Registered devices", path: ROUTES.devices, Icon: DevicesOutlinedIcon, kw: "drones hardware register device" },
  { title: "Thrust Stand", subtitle: "Thrust stand tools", path: ROUTES.thrustStand, Icon: SpeedOutlinedIcon, kw: "thrust stand motor esc bench" },
  { title: "Log Analyzer", subtitle: "Flight log analysis", path: ROUTES.logAnalyzer, Icon: InsightsOutlinedIcon, kw: "flight log bin tlog analyze telemetry crash" },
  { title: "Propulsion Calculator", subtitle: "Motor, prop & battery sizing", path: ROUTES.propulsionCalc, Icon: CalculateOutlinedIcon, kw: "motor prop propeller esc battery thrust twr flight time sizing" },
  { title: "Motor Data", subtitle: "Claimed vs measured test data", path: ROUTES.motorData, Icon: TableChartOutlinedIcon, kw: "motor propeller bench test thrust curve claimed measured" },
  { title: "Firmware Flasher", subtitle: "Flash ArduPilot over USB", path: ROUTES.firmwareFlasher, Icon: MemoryOutlinedIcon, kw: "firmware flash ardupilot board usb serial" },
  { title: "Billing", subtitle: "Payments & invoices", path: ROUTES.plansBilling, Icon: CreditCardOutlinedIcon, kw: "payment invoice subscription card charges" },
  { title: "Plans", subtitle: "Subscription plans", path: ROUTES.plans, Icon: CreditCardOutlinedIcon, kw: "upgrade subscription pricing plan" },
  { title: "Profile", subtitle: "Your account details", path: ROUTES.profile, Icon: PersonOutlineIcon, kw: "account name email personal" },
];

const deviceName = (d) => d.device_name || d.deviceName || d.name || "Device";
const deviceType = (d) => d.device_type || d.deviceType || "Device";
const normalise = (value) => String(value || "").toLowerCase();
const queryTokens = (value) => normalise(value).split(/[^a-z0-9/_{}.-]+/).filter(Boolean);
const contentIcon = (kind) => (kind === "api" ? HttpOutlinedIcon : ArticleOutlinedIcon);

const scoreContentHit = (entry, rawQuery) => {
  const queryText = normalise(rawQuery).trim();
  if (!queryText) return 0;

  const title = normalise(entry.title);
  const subtitle = normalise(entry.subtitle);
  const path = normalise(entry.path);
  const text = normalise(entry.text);
  const haystack = `${title} ${subtitle} ${path} ${text}`;
  const tokens = queryTokens(queryText);
  let score = 0;

  if (title === queryText) score += 120;
  if (title.includes(queryText)) score += 70;
  if (path.includes(queryText)) score += 65;
  if (subtitle.includes(queryText)) score += 45;
  if (text.includes(queryText)) score += 35;

  tokens.forEach((token) => {
    if (title.includes(token)) score += 18;
    if (path.includes(token)) score += 16;
    if (subtitle.includes(token)) score += 10;
    if (text.includes(token)) score += 6;
  });

  if (tokens.length && tokens.every((token) => haystack.includes(token))) score += 20;
  return score;
};

const loadRecents = () => {
  try {
    const v = JSON.parse(window.localStorage.getItem(RECENTS_KEY) || "[]");
    return Array.isArray(v) ? v.slice(0, 5) : [];
  } catch (_) {
    return [];
  }
};
const saveRecent = (q) => {
  try {
    const next = [q, ...loadRecents().filter((x) => x !== q)].slice(0, 5);
    window.localStorage.setItem(RECENTS_KEY, JSON.stringify(next));
  } catch (_) {
    /* no-op */
  }
};

function Highlight({ text, q }) {
  if (!q) return <>{text}</>;
  const i = text.toLowerCase().indexOf(q.toLowerCase());
  if (i < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, i)}
      <b>{text.slice(i, i + q.length)}</b>
      {text.slice(i + q.length)}
    </>
  );
}

export default function NavSearch() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { openPanel, send } = useAssistant();
  const records = useAssistantRecords();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [recents, setRecents] = useState(loadRecents());
  const inputRef = useRef(null);

  const q = query.trim().toLowerCase();

  const pageResults = useMemo(() => {
    if (!q) return [];
    return INDEX.filter((it) => it.title.toLowerCase().includes(q) || it.kw.includes(q)).slice(0, 6);
  }, [q]);

  const deviceResults = useMemo(() => {
    if (!q) return [];
    return (records.devices || [])
      .filter((d) => deviceName(d).toLowerCase().includes(q))
      .slice(0, 5);
  }, [q, records.devices]);

  const contentResults = useMemo(() => {
    if (!q) return [];
    return CONTENT_SEARCH_INDEX.map((entry) => ({
      ...entry,
      score: scoreContentHit(entry, query.trim()),
      Icon: contentIcon(entry.kind),
    }))
      .filter((entry) => entry.score > 0)
      .sort((a, b) => b.score - a.score || a.title.localeCompare(b.title))
      .slice(0, 8);
  }, [q, query]);

  // Flat list of clickable options (for keyboard nav), in render order.
  const options = useMemo(() => {
    const pages = pageResults.map((r) => ({ kind: "page", title: r.title, subtitle: r.subtitle, path: r.path, Icon: r.Icon }));
    const devs = deviceResults.map((d) => ({
      kind: "device",
      title: deviceName(d),
      subtitle: deviceType(d),
      path: ROUTES.devices,
      Icon: DevicesOutlinedIcon,
    }));
    const content = contentResults.map((r) => ({
      kind: r.kind,
      title: r.title,
      subtitle: r.subtitle,
      path: r.path,
      Icon: r.Icon,
    }));
    return [...pages, ...devs, ...content];
  }, [pageResults, deviceResults, contentResults]);

  const askIndex = options.length;

  useEffect(() => setActive(0), [q]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
        records.load();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const close = () => setOpen(false);

  const goTo = (opt) => {
    saveRecent(query.trim() || opt.title);
    setRecents(loadRecents());
    navigate(opt.path);
    setQuery("");
    close();
  };

  const askAssistant = () => {
    const text = query.trim();
    saveRecent(text);
    setRecents(loadRecents());
    openPanel();
    if (text) send(text);
    setQuery("");
    close();
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, askIndex));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (active === askIndex || options.length === 0) askAssistant();
      else goTo(options[active]);
    } else if (e.key === "Escape") {
      close();
    }
  };

  const rowBase = {
    display: "flex",
    alignItems: "center",
    gap: 1.25,
    px: 1.5,
    py: 1,
    borderRadius: "8px",
    cursor: "pointer",
    color: theme.palette.text.color,
  };
  const activeSx = { backgroundColor: theme.palette.tertiary.main };
  const sectionLabel = {
    px: 1.5,
    py: 0.75,
    fontSize: 11,
    fontWeight: 600,
    color: theme.palette.secondaryText.color,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  };

  // Renders one result row given its flat option index (drives keyboard highlight).
  const renderRow = (opt, idx) => (
    <Box
      key={opt.kind + opt.path + opt.title + idx}
      sx={{ ...rowBase, ...(active === idx ? activeSx : {}) }}
      onMouseEnter={() => setActive(idx)}
      onClick={() => goTo(opt)}
    >
      <opt.Icon sx={{ fontSize: 20, color: theme.palette.secondaryText.color }} />
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 14, lineHeight: 1.2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          <Highlight text={opt.title} q={query.trim()} />
        </Typography>
        <Typography sx={{ fontSize: 12, color: theme.palette.secondaryText.color }}>
          {opt.subtitle}
        </Typography>
      </Box>
    </Box>
  );

  const devOffset = pageResults.length;
  const contentOffset = pageResults.length + deviceResults.length;

  return (
    <ClickAwayListener onClickAway={close}>
      <Box sx={{ mr: "auto", position: "relative", flex: "1 1 auto", maxWidth: 460, minWidth: 120 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            height: "34px",
            px: 1.25,
            borderRadius: "10px",
            border: `1px solid ${open ? ACCENT : theme.palette.border.color}`,
            backgroundColor: theme.palette.primary.main,
          }}
        >
          <SearchIcon sx={{ fontSize: 18, color: theme.palette.secondaryText.color }} />
          <InputBase
            inputRef={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => {
              setOpen(true);
              records.load();
            }}
            onKeyDown={onKeyDown}
            placeholder="Find or ask anything…"
            sx={{ flex: 1, fontSize: 14, color: theme.palette.text.color }}
            inputProps={{ "aria-label": "Find or ask" }}
          />
          <Box
            component="span"
            sx={{
              fontSize: 11,
              fontFamily: "monospace",
              color: theme.palette.secondaryText.color,
              border: `1px solid ${theme.palette.border.color}`,
              borderRadius: "5px",
              px: 0.5,
            }}
          >
            ⌘K
          </Box>
        </Box>

        {open && (
          <Box
            sx={{
              position: "absolute",
              top: "calc(100% + 6px)",
              left: 0,
              right: 0,
              zIndex: 1300,
              maxHeight: "66vh",
              overflow: "auto",
              backgroundColor: theme.palette.secondary.main,
              border: `1px solid ${theme.palette.border.color}`,
              borderRadius: "12px",
              boxShadow: "0 16px 48px rgba(0,0,0,0.20)",
              p: 1,
            }}
          >
            {q === "" ? (
              recents.length ? (
                <>
                  <Typography sx={sectionLabel}>Recent searches</Typography>
                  {recents.map((r) => (
                    <Box key={r} sx={rowBase} onClick={() => setQuery(r)}>
                      <HistoryOutlinedIcon sx={{ fontSize: 18, color: theme.palette.secondaryText.color }} />
                      <Typography sx={{ fontSize: 14 }}>{r}</Typography>
                    </Box>
                  ))}
                </>
              ) : (
                <Typography sx={{ px: 1.5, py: 1.5, fontSize: 13, color: theme.palette.secondaryText.color }}>
                  Search pages and devices — or ask the assistant.
                </Typography>
              )
            ) : (
              <>
                {pageResults.length > 0 && (
                  <>
                    <Typography sx={sectionLabel}>Page jumps</Typography>
                    {pageResults.map((r, i) => renderRow(options[i], i))}
                  </>
                )}

                {deviceResults.length > 0 && (
                  <>
                    <Typography sx={sectionLabel}>Devices</Typography>
                    {deviceResults.map((_, i) => renderRow(options[devOffset + i], devOffset + i))}
                  </>
                )}

                {contentResults.length > 0 && (
                  <>
                    <Typography sx={sectionLabel}>Content matches</Typography>
                    {contentResults.map((_, i) => renderRow(options[contentOffset + i], contentOffset + i))}
                  </>
                )}

                {options.length === 0 && (
                  <Typography sx={{ px: 1.5, pt: 1, pb: 0.5, fontSize: 13, color: theme.palette.secondaryText.color }}>
                    {!records.loaded ? "Searching…" : `No matches for “${query.trim()}”.`}
                  </Typography>
                )}

                {/* AI fallback — always available */}
                <Box
                  sx={{ ...rowBase, mt: 0.5, ...(active === askIndex ? activeSx : {}) }}
                  onMouseEnter={() => setActive(askIndex)}
                  onClick={askAssistant}
                >
                  <AutoAwesomeIcon sx={{ fontSize: 20, color: ACCENT }} />
                  <Typography sx={{ fontSize: 14 }}>
                    Ask the assistant: <b>“{query.trim()}”</b>
                  </Typography>
                </Box>
              </>
            )}

            <Box
              sx={{
                display: "flex",
                gap: 2,
                px: 1.5,
                pt: 1,
                mt: 0.5,
                borderTop: `1px solid ${theme.palette.border.color}`,
                fontSize: 11,
                color: theme.palette.secondaryText.color,
              }}
            >
              <span>↑↓ Navigate</span>
              <span>↵ Open</span>
              <span>Esc Close</span>
            </Box>
          </Box>
        )}
      </Box>
    </ClickAwayListener>
  );
}
